import type { ReactNode } from 'react';

/**
 * One setting as a row (spec 198): what it is on the left, the control on the
 * right, in a card. The left column says the setting's name and one line of
 * what it means, so the page reads as a list of things one can change, not a
 * stack of boxes to open. On a phone the two stack.
 *
 * Every row wears the same card (spec 228 FR-012): a setting is never drawn on
 * a red surface. The one action that cannot be taken back is told apart by its
 * destructive button and the dialog behind it, not by the card around it.
 */
export function SettingsRow({
  id,
  title,
  lead,
  children,
}: {
  /** The heading's id; the section is labelled by it. */
  id: string;
  title: string;
  /** One line under the title: what the setting means. */
  lead?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      aria-labelledby={id}
      className="grid gap-3 md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] md:gap-8"
    >
      <div className="flex flex-col gap-1 md:pt-4">
        <h2 id={id} className="text-foreground text-sm font-medium">
          {title}
        </h2>
        {lead ? (
          <p className="text-muted-foreground text-xs leading-relaxed">
            {lead}
          </p>
        ) : null}
      </div>
      <div className="border-border bg-card flex min-w-0 flex-col gap-3 rounded-lg border p-4">
        {children}
      </div>
    </section>
  );
}
